import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AvatarOrderDataResponse, AvatarOrderRequest, AvatarOrderResponse, Client } from '@api/client';
import { CALL_ORDER_STATUS_NUMBERS, CALL_ORDER_TYPE_NUMBERS } from '../call.constants';
import { EMPTY, firstValueFrom, Observable, takeUntil, timeout } from 'rxjs';
import { ConfigService } from '@services/config.service';
import { CallOrderUpdatesService } from '@app/services/call-order-updates.service';
import { AudioCallFinalizationService } from '@app/services/audio-call-finalization.service';

interface CallOrderSummaryItem {
  id: number;
  status: number | null;
  type: number | null;
  realTime: number | null;
}

interface CallOrderSummaryResponse {
  data?: CallOrderSummaryItem[] | null;
}

@Injectable({
  providedIn: 'root'
})
export class CallOrderService {
  private readonly client: Client = inject(Client);
  private readonly callOrderUpdates = inject(CallOrderUpdatesService);
  private readonly finalization = inject(AudioCallFinalizationService);
  private readonly http: HttpClient = inject(HttpClient);
  private readonly configService: ConfigService = inject(ConfigService);
  private readonly cachedOrders: Map<number, AvatarOrderDataResponse> = new Map<number, AvatarOrderDataResponse>();

  private get apiBaseUrl(): string {
    return this.configService.urlBackEndApi.replace(/\/+$/, '');
  }

  public async getOrder(orderId: number | string, useCache: boolean = true): Promise<AvatarOrderDataResponse> {
    const normalizedOrderId: number = this.normalizeOrderId(orderId);
    if (useCache) {
      const finalizedOrder = this.callOrderUpdates.getUpdatedOrder(normalizedOrderId);
      if (finalizedOrder) {
        this.cachedOrders.delete(normalizedOrderId);
        return new AvatarOrderDataResponse({data: finalizedOrder});
      }
      const cachedOrder: AvatarOrderDataResponse | undefined = this.cachedOrders.get(normalizedOrderId);
      if (cachedOrder) {
        this.cachedOrders.delete(normalizedOrderId);
        return cachedOrder;
      }
    }

    const response: AvatarOrderDataResponse = await firstValueFrom(this.client.avatarOrderGet(normalizedOrderId));
    if (useCache) {
      this.cachedOrders.set(normalizedOrderId, response);
    }
    return response;
  }

  public async getOrdersSummary(orderIds: number[]): Promise<AvatarOrderResponse[]> {
    const uniqueIds: number[] = [];
    const seen: Set<number> = new Set<number>();

    for (const id of orderIds) {
      if (typeof id !== 'number' || !Number.isFinite(id)) continue;
      if (seen.has(id)) continue;
      seen.add(id);
      uniqueIds.push(id);
    }

    if (uniqueIds.length === 0) return [];

    const response: CallOrderSummaryResponse = await firstValueFrom(
      this.http.post<CallOrderSummaryResponse>(`${this.apiBaseUrl}/avatarOrder/summary`, {
        ids: uniqueIds,
      })
    );

    const items: CallOrderSummaryItem[] = Array.isArray(response?.data) ? response.data : [];
    const mapped: AvatarOrderResponse[] = [];

    for (const item of items) {
      const id = item?.id;
      if (typeof id !== 'number' || !Number.isFinite(id)) continue;

      const status = (typeof item.status === 'number' && Number.isFinite(item.status)) ? item.status : undefined;
      const type = (typeof item.type === 'number' && Number.isFinite(item.type)) ? item.type : undefined;
      const realTime = (typeof item.realTime === 'number' && Number.isFinite(item.realTime)) ? item.realTime : undefined;
      mapped.push(AvatarOrderResponse.fromJS({id, status, type, realTime}));
    }

    return mapped;
  }

  public isOrderActive(status: number | undefined): boolean {
    return status === CALL_ORDER_STATUS_NUMBERS.CREATED
      || status === CALL_ORDER_STATUS_NUMBERS.ACCEPTED;
  }

  public isOrderAccepted(status: number | undefined): boolean {
    return status === CALL_ORDER_STATUS_NUMBERS.ACCEPTED;
  }

  public isValidOrder(order: AvatarOrderResponse | null | undefined): boolean {
    return this.isOrderActive(order?.status) && this.hasRequiredFields(order);
  }

  public isOrderFinal(status: number | undefined): boolean {
    return status === CALL_ORDER_STATUS_NUMBERS.REJECTED
      || status === CALL_ORDER_STATUS_NUMBERS.COMPLETED
      || status === CALL_ORDER_STATUS_NUMBERS.CANCELED;
  }

  public isAudioCallOrder(order: AvatarOrderResponse | null | undefined): boolean {
    return order?.type === CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL;
  }

  public async createOutgoingAudioCallOrder(
    toUserId: number, dateFromAt: string, attemptId: string, userId: number,
    cancel$: Observable<void> = EMPTY,
  ): Promise<AvatarOrderDataResponse> {
    const request: AvatarOrderRequest = new AvatarOrderRequest({
      to_id: toUserId,
      type: CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL,
      date_from_at: dateFromAt,
      is_shared: false,
      call_attempt_id: attemptId,
      call_actor_id: userId,
    });

    try {
      const response = await firstValueFrom(this.client.avatarOrderPost(request).pipe(timeout(8000), takeUntil(cancel$)));
      if (!response?.data?.id) throw new Error('No order id in response');
      return response;
    } catch (error) {
      // An aborted POST can still commit. Serialize cleanup by attempt before
      // releasing the caller's start guard, even when no order id was received.
      await this.finalization.cancelStart(toUserId, userId, attemptId);
      throw error;
    }
  }

  public async cancelAudioCallOrder(orderId: number): Promise<AvatarOrderDataResponse> {
    const response = await firstValueFrom(this.client.avatarOrderCancel(orderId));
    this.cachedOrders.delete(orderId);
    if (response.data) this.callOrderUpdates.updateOrder(response.data);
    return response;
  }

  private hasRequiredFields(order: AvatarOrderResponse | null | undefined): boolean {
    if (!order) return false;

    const fromId: number | undefined = order?.from?.id;
    if (!fromId) {
      console.error('Order from id is not defined!');
      return false;
    }

    return this.isAudioCallOrder(order);
  }

  private normalizeOrderId(orderId: number | string): number {
    if (typeof orderId === 'number') {
      return orderId;
    }
    const parsedOrderId: number = Number(orderId);
    if (Number.isNaN(parsedOrderId)) {
      throw new Error('Invalid order id');
    }
    return parsedOrderId;
  }
}
